import environment from '@/lib/environment';

const DEFAULT_TIMEZONE = environment.timezone;
const DEFAULT_LOCALE = environment.locale;

const DATE_PART_OPTIONS = {
  timeZone: DEFAULT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
} as const;

const DATE_TIME_PART_OPTIONS = {
  ...DATE_PART_OPTIONS,
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
} as const;

const DATE_TIME_SHORT_PART_OPTIONS = {
  ...DATE_PART_OPTIONS,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
} as const;

const DATE_FORMATTER_BY_LOCALE: Record<string, Intl.DateTimeFormat> = {
  'fi-FI': new Intl.DateTimeFormat('fi-FI', DATE_PART_OPTIONS),
  fi: new Intl.DateTimeFormat('fi-FI', DATE_PART_OPTIONS),
  'en-GB': new Intl.DateTimeFormat('en-GB', DATE_PART_OPTIONS),
  en: new Intl.DateTimeFormat('en-GB', DATE_PART_OPTIONS),
  'en-US': new Intl.DateTimeFormat('en-US', DATE_PART_OPTIONS),
  'sv-SE': new Intl.DateTimeFormat('sv-SE', DATE_PART_OPTIONS),
  sv: new Intl.DateTimeFormat('sv-SE', DATE_PART_OPTIONS),
};

const DATE_TIME_FORMATTER_BY_LOCALE: Record<string, Intl.DateTimeFormat> = {
  'fi-FI': new Intl.DateTimeFormat('fi-FI', DATE_TIME_PART_OPTIONS),
  fi: new Intl.DateTimeFormat('fi-FI', DATE_TIME_PART_OPTIONS),
  'en-GB': new Intl.DateTimeFormat('en-GB', DATE_TIME_PART_OPTIONS),
  en: new Intl.DateTimeFormat('en-GB', DATE_TIME_PART_OPTIONS),
  'en-US': new Intl.DateTimeFormat('en-US', DATE_TIME_PART_OPTIONS),
  'sv-SE': new Intl.DateTimeFormat('sv-SE', DATE_TIME_PART_OPTIONS),
  sv: new Intl.DateTimeFormat('sv-SE', DATE_TIME_PART_OPTIONS),
};

const DATE_TIME_SHORT_FORMATTER_BY_LOCALE: Record<string, Intl.DateTimeFormat> = {
  'fi-FI': new Intl.DateTimeFormat('fi-FI', DATE_TIME_SHORT_PART_OPTIONS),
  fi: new Intl.DateTimeFormat('fi-FI', DATE_TIME_SHORT_PART_OPTIONS),
  'en-GB': new Intl.DateTimeFormat('en-GB', DATE_TIME_SHORT_PART_OPTIONS),
  en: new Intl.DateTimeFormat('en-GB', DATE_TIME_SHORT_PART_OPTIONS),
  'en-US': new Intl.DateTimeFormat('en-US', DATE_TIME_SHORT_PART_OPTIONS),
  'sv-SE': new Intl.DateTimeFormat('sv-SE', DATE_TIME_SHORT_PART_OPTIONS),
  sv: new Intl.DateTimeFormat('sv-SE', DATE_TIME_SHORT_PART_OPTIONS),
};

const CURRENCY_FORMATTER_BY_LOCALE: Record<string, Intl.NumberFormat> = {
  'fi-FI': new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }),
  fi: new Intl.NumberFormat('fi-FI', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }),
  'en-GB': new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }),
  en: new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }),
  'en-US': new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }),
  'sv-SE': new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }),
  sv: new Intl.NumberFormat('sv-SE', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }),
};

export const REPAIR_HISTORY_YEAR_FORMATTER = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
  timeZone: DEFAULT_TIMEZONE,
  year: 'numeric',
});

export const DATE_ONLY_FORMATTER = new Intl.DateTimeFormat('en-CA', {
  timeZone: DEFAULT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

export const TIME_ONLY_FORMATTER = new Intl.DateTimeFormat('en-GB', {
  timeZone: DEFAULT_TIMEZONE,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

export const DATE_FILTER_FORMATTER = new Intl.DateTimeFormat('fi-FI', {
  timeZone: DEFAULT_TIMEZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

function resolveLocaleFormatter<T extends Intl.DateTimeFormat | Intl.NumberFormat>(
  formatters: Record<string, T>,
  locale: string,
  fallbackLocale: string,
): T {
  return formatters[locale] ?? formatters[locale.split('-')[0]] ?? formatters[fallbackLocale];
}

export function getDateFormatter(locale = DEFAULT_LOCALE): Intl.DateTimeFormat {
  return resolveLocaleFormatter(DATE_FORMATTER_BY_LOCALE, locale, DEFAULT_LOCALE);
}

export function getDateTimeFormatter(locale = DEFAULT_LOCALE): Intl.DateTimeFormat {
  return resolveLocaleFormatter(DATE_TIME_FORMATTER_BY_LOCALE, locale, DEFAULT_LOCALE);
}

export function getDateTimeShortFormatter(locale = DEFAULT_LOCALE): Intl.DateTimeFormat {
  return resolveLocaleFormatter(DATE_TIME_SHORT_FORMATTER_BY_LOCALE, locale, DEFAULT_LOCALE);
}

export function getCurrencyFormatter(locale = DEFAULT_LOCALE): Intl.NumberFormat {
  return resolveLocaleFormatter(CURRENCY_FORMATTER_BY_LOCALE, locale, DEFAULT_LOCALE);
}
